
import { DragStartEvent, DragOverEvent, DragEndEvent } from '@dnd-kit/core';
import { arrayMove } from '@dnd-kit/sortable';
import { useState } from 'react';
import { PipelineColumn, Prospect, PipelineStage } from '@/types/pipeline';
import { useUpdateProspectStatus } from './use-pipeline-data';
import { useToast } from '@/hooks/errors/use-toast';
import { usePipelineStages } from './use-pipeline-stages';

export const usePipelineDrag = (
  initialColumns: PipelineColumn[], 
  setColumns: React.Dispatch<React.SetStateAction<PipelineColumn[]>>
) => {
  const [activeProspect, setActiveProspect] = useState<Prospect | null>(null);
  const [isUpdating, setIsUpdating] = useState(false);
  const { updateStatus } = useUpdateProspectStatus();
  const { toast } = useToast();
  const { pipelineStages } = usePipelineStages();

  const handleDragStart = (event: DragStartEvent) => {
    const { active } = event;
    const id = active.id as string;

    // Find the active prospect
    for (const column of initialColumns) {
      const foundProspect = column.prospects.find(proj => proj.id === id);
      if (foundProspect) {
        setActiveProspect(foundProspect);
        break;
      }
    }
  };

  const handleDragOver = (event: DragOverEvent) => {
    const { active, over } = event;
    if (!over) return;

    const activeId = active.id as string;
    const overId = over.id as string;

    // Find the source and destination columns
    const activeColumnId = findColumnByProspectId(activeId, initialColumns);
    const overColumnId = findColumnByProspectId(overId, initialColumns) || overId;

    if (!activeColumnId || activeColumnId === overColumnId) return;

    setColumns((prevColumns: PipelineColumn[]) => {
      // Get the prospect
      const activeColumn = prevColumns.find(col => col.id === activeColumnId);
      if (!activeColumn) return prevColumns;

      const activeProspect = activeColumn.prospects.find(p => p.id === activeId);
      if (!activeProspect) return prevColumns;

      // Remove the prospect from the active column
      const updatedActiveColumn = {
        ...activeColumn,
        prospects: activeColumn.prospects.filter(p => p.id !== activeId)
      };

      // Add the prospect to the new column with updated status
      const updatedProspect = { ...activeProspect, stage: overColumnId as PipelineStage };

      // Create the updated columns array
      return prevColumns.map(col => {
        if (col.id === activeColumnId) return updatedActiveColumn;
        if (col.id === overColumnId) {
          return {
            ...col,
            prospects: [...col.prospects, updatedProspect]
          };
        }
        return col;
      });
    });
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    
    setActiveProspect(null);
    
    if (!over) return;
    
    const activeId = active.id as string;
    const overId = over.id as string;
    
    // If dropping on a column directly
    if (isColumnId(overId, initialColumns)) {
      try {
        setIsUpdating(true);
        console.log(`[DragEnd] Moving prospect ${activeId} to column ${overId}`);
        
        // Update the prospect status in the database - use new signature
        await updateStatus({ id: activeId, newStatus: overId as PipelineStage });
        
        toast({
          title: "Status Updated",
          description: `Prospect moved to ${getColumnTitle(overId, initialColumns)}`,
          variant: "success",
        });
      } catch (error) {
        console.error('[DragEnd] Error updating status:', error);
        toast({
          title: "Update Failed",
          description: "Could not move the prospect. Please try again.",
          variant: "error",
        });
      } finally {
        setIsUpdating(false);
      }
      return;
    }
    
    // Reordering within the same column
    const activeColumnId = findColumnByProspectId(activeId, initialColumns);
    const overColumnId = findColumnByProspectId(overId, initialColumns);
    
    if (!activeColumnId || !overColumnId || activeColumnId !== overColumnId) return;
    
    setColumns((prevColumns: PipelineColumn[]) => {
      const column = prevColumns.find(col => col.id === activeColumnId);
      if (!column) return prevColumns;
      
      const oldIndex = column.prospects.findIndex(p => p.id === activeId);
      const newIndex = column.prospects.findIndex(p => p.id === overId);
      
      if (oldIndex === -1 || newIndex === -1) return prevColumns;
      
      const updatedColumn = {
        ...column,
        prospects: arrayMove(column.prospects, oldIndex, newIndex)
      };
      
      return prevColumns.map(col => col.id === activeColumnId ? updatedColumn : col);
    });
  };

  return {
    activeProspect,
    isUpdating,
    handleDragStart,
    handleDragOver,
    handleDragEnd
  };
};

// Helper function to find which column a prospect belongs to
const findColumnByProspectId = (prospectId: string, columns: PipelineColumn[]): string | undefined => {
  for (const column of columns) {
    if (column.prospects.some(prospect => prospect.id === prospectId)) {
      return column.id;
    }
  }
  return undefined;
};

// Helper function to check if an ID belongs to a column
const isColumnId = (id: string, columns: PipelineColumn[]): boolean => {
  return columns.some(column => column.id === id);
};

// Helper function to get a column title by ID
const getColumnTitle = (columnId: string, columns: PipelineColumn[]): string => {
  const column = columns.find(col => col.id === columnId);
  return column?.title || 'Unknown';
};
